import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import { setWebTitle, todayStart } from '@/lib/format-utils';
import { useService } from '@/hooks/api-hooks';
import { ServiceTitleStatic } from '@/components/services/service-title';
import PageHeader from '@/components/page-header';
import { SkeletonListWithFilters } from '@/components/snippets';
import { NotFound } from '@/components/not-found';
import type { Service } from '@turizi/shared';
import ServiceCardCatalog from '@/components/service-card-catalog';
import { getServiceSellPrice } from '@/lib/models-utils';
import {
    IconInfoCircle,
    IconCoin,
    IconTag,
    IconShoppingCartCancel,
    IconPhoto,
    IconCalendar,
    IconAdjustmentsHorizontal,
    IconBuildingStore,
    IconAlertTriangle,
    IconCalculator,
    IconCoinFilled,
    IconReceiptFilled,
} from '@tabler/icons-react';


export default function EditService() {
    const { t } = useTranslation();

    const { id } = useParams<{ id: string }>();

    const {
        data,
        isLoading,
        error
    } = useService(id);
    const service = data?.data;

    setWebTitle(`${t('service')} #${id} - ${t(service?.category)} ${ServiceTitleStatic(service)}`);

    const sectionItems = useMemo(() => [
        { key: 'details', label: t('details'), icon: IconInfoCircle },
        { key: 'operations', label: t('operations'), icon: IconAdjustmentsHorizontal },
        { key: 'providers', label: t('providers'), icon: IconBuildingStore },
        { key: 'price_generator', label: t('price_generator'), icon: IconCalculator },
        { key: 'prices_and_costs', label: t('prices_and_costs'), icon: IconCoin },
        // { key: 'prices', label: t('prices'), icon: IconCoinFilled },
        // { key: 'costs', label: t('costs'), icon: IconReceiptFilled },
        { key: 'discounts', label: t('service_discounts'), icon: IconTag },
        { key: 'booking_limits', label: t('booking_limits'), icon: IconShoppingCartCancel },
        { key: 'images', label: t('images'), icon: IconPhoto },
        { key: 'bookings', label: t('bookings'), icon: IconCalendar },
    ], [t]);

    const validationErrors = useMemo(() => {
        if (!service) return [];
        const errors: string[] = [];
        const today = todayStart();

        // 1. Has costs but no provider
        const hasCosts = service.sell_costs && service.sell_costs.length > 0;
        const hasProviders = service.providers && service.providers.length > 0;
        if (hasCosts && !hasProviders) {
            errors.push(t('validation_costs_without_providers'));
        }

        // 3. Has no "price_by" nor "cost_by"
        const hasPriceBy = Boolean(service.price_by && service.price_by.trim() !== '');
        const hasCostBy = Boolean(service.cost_by && service.cost_by.trim() !== '');
        if (!hasPriceBy && !hasCostBy) {
            errors.push(t('validation_no_price_by_nor_cost_by'));
        }

        // 4. Has no active sell price (all prices are in the past)
        const activePrices = getServiceSellPrice(service, today);
        if (!activePrices || activePrices.length === 0) {
            errors.push(t('validation_no_active_sell_price'));
        }

        return errors;
    }, [service, t]);

    if (isLoading && !service) {
        return (
            <div>
                <PageHeader
                    title={t('service')}
                />
                <div className="m-4">
                    <SkeletonListWithFilters />
                </div>
            </div>
        )
    }

    if (!isLoading && !service) {
        return <NotFound type="service" />;
    }

    return (
        <div className="space-y-6">
            <div>
                <ServiceCardCatalog
                    service={service}
                    iconSize="xs"
                    showProviders={false}
                    showSoldBy={false}
                />
                {validationErrors.length > 0 && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                        {validationErrors.map((errMsg, idx) => (
                            <div
                                key={idx}
                                className="flex items-start gap-2.5 bg-red-50/90 border border-red-200/90 text-red-950 rounded-xl p-3 text-xs font-medium shadow-2xs"
                            >
                                <IconAlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                                <div className="flex-1 leading-relaxed">{errMsg}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {
                error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mx-4">
                        {error?.message}
                    </div>
                )
            }

            <div className="flex flex-col md:flex-row gap-2 md:gap-2">
                {/* Left Side Menu (Sidebar) */}
                <aside className="w-full md:w-[200px] shrink-0">
                    <div className="sticky top-20 flex flex-row md:flex-col gap-1 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0 border-b border-gray-200 md:border-b-0">
                        {sectionItems.map((section) => {
                            const Icon = section.icon;
                            return (
                                <NavLink
                                    key={section.key}
                                    to={section.key}
                                    className={({ isActive }) =>
                                        `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-150 ${isActive
                                            ? 'bg-gray-100 text-gray-900 shadow-xs'
                                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50/80'
                                        }`
                                    }
                                    end
                                >
                                    {({ isActive }) => (
                                        <>
                                            <Icon size={16} className={isActive ? 'text-gray-900' : 'text-gray-400'} stroke={2} />
                                            <span className={isActive ? 'text-gray-900' : 'text-gray-500'}>{section.label}</span>
                                        </>
                                    )}
                                </NavLink>
                            );
                        })}
                    </div>
                </aside>

                {/* Right Side Content Column */}
                <main className="flex-1 min-w-0">
                    <Outlet context={service as Service} />
                </main>
            </div>
        </div >
    );
}